import { AssistantMessage, ChatLog, Composer, ErrorNotice, GreetingBubble, UserBubble, answerWorkflowStep, useWorkflowComposer, workflowReplyComposer, workflowReplyIssue, type ChatMessage, type ChatStage, type ErrorPayload, type SendOptions } from "@sagot/chat";
import { useTranslation } from "@sagot/i18n";
import { Badge, Button, Icon } from "@sagot/ui";
import { useState, type ReactNode } from "react";
import { useWorkflowCopy } from "./workflow-copy";

interface ChatPaneProps {
  agentName: string;
  subtitle: string;
  /** The agent's welcome message: the first assistant bubble (UI-only, never sent to the model). */
  greeting: string;
  /** Starter questions, shown as chips under the greeting until the first question. */
  starters: readonly string[];
  messages: ChatMessage[];
  busy: boolean;
  error: ErrorPayload | null;
  /** Replaces the generic "Something went wrong" line when the page knows why the message failed. */
  errorText?: string;
  onSend: (text: string, options?: SendOptions) => void;
  onStop: () => void;
  onRetry: () => void;
  onClear: () => void;
  /** Opens the config sheet (< lg) and the trace sheet (< xl). */
  configTrigger: ReactNode;
  traceTrigger: ReactNode;
}

export function useStageLabels(): Partial<Record<ChatStage, string>> {
  const { t } = useTranslation();
  return {
    understanding: t("build.playground.stages.understanding"),
    retrieving: t("build.playground.stages.retrieving"),
    reranking: t("build.playground.stages.reranking"),
    generating: t("build.playground.stages.generating"),
    validating: t("build.playground.stages.validating"),
  };
}

/** Center pane: header, streamed conversation (`aria-live`), composer. */
export function ChatPane({ agentName, subtitle, greeting, starters, messages, busy, error, errorText, onSend, onStop, onRetry, onClear, configTrigger, traceTrigger }: ChatPaneProps) {
  const { t } = useTranslation();
  const stageLabels = useStageLabels();
  const workflowLabels = useWorkflowCopy();
  const workflowComposer = useWorkflowComposer(messages);
  const replyCard = workflowComposer.mode === "reply" ? workflowComposer.card : undefined;
  /** Why the composer refused the text typed for the open question (a code that is not its digits); the Playground has no bot check. */
  const [replyIssue, setReplyIssue] = useState<string | null>(null);
  /** The composer answers the open question when it asks for one, else it sends a message. */
  const submit = (text: string) => {
    if (!replyCard) {
      onSend(text);
      return;
    }
    if (answerWorkflowStep(onSend, replyCard, { value: text })) {
      setReplyIssue(null);
      return;
    }
    const issue = workflowReplyIssue(replyCard, text, workflowLabels);
    setReplyIssue(issue);
    if (issue === null) onSend(text); // not a code: the server asks again (five of them end the step)
  };
  return (
    <section className="flex min-w-0 flex-1 flex-col bg-surface" aria-label={t("build.playground.chat.log")}>
      <div className="flex h-14 shrink-0 items-center gap-2 border-b border-line px-4">
        {configTrigger}
        <span aria-hidden="true" className="inline-flex size-7 shrink-0 items-center justify-center rounded-pill bg-ink-950 text-brand-500 dark:bg-brand-500 dark:text-ink-950"><Icon name="sparkles" className="size-3.5" /></span>
        <div className="min-w-0">
          <h1 className="truncate text-sm font-bold">{agentName}</h1>
          <p className="truncate text-[11px] text-fg-subtle" data-testid="playground-subtitle">{subtitle}</p>
        </div>
        <Badge tone="warning" className="ml-1 hidden sm:inline-flex"><Icon name="eye-off" className="size-3" />{t("build.playground.chat.badge")}</Badge>
        <div className="ml-auto flex items-center gap-1.5">
          {traceTrigger}
          <Button variant="ghost" size="sm" onClick={onClear} aria-label={t("build.playground.chat.clear")} disabled={messages.length === 0}>
            <Icon name="refresh-cw" />
            <span className="hidden 2xl:inline">{t("build.playground.chat.clear")}</span>
          </Button>
        </div>
      </div>
      <ChatLog busy={busy} label={t("build.playground.chat.log")} className="min-h-0 flex-1">
        <div className="mx-auto max-w-2xl space-y-5 px-4 py-6">
          <GreetingBubble
            name={agentName}
            message={greeting}
            starters={messages.length === 0 ? starters : []}
            onAsk={onSend}
            busy={busy}
            startersLabel={t("build.playground.chat.starters")}
          />
          {messages.length === 0 ? <p className="px-1 text-center text-xs text-fg-subtle">{t("build.playground.chat.welcomeBody")}</p> : null}
          {messages.map((message) =>
            message.role === "user" ? (
              <UserBubble key={message.id} text={message.text} />
            ) : (
              <AssistantMessage
                key={message.id}
                message={message}
                botName={agentName}
                onAsk={onSend}
                audience="staff"
                stageLabels={stageLabels}
                relatedLabel={t("chat.related")}
                busy={busy}
                detailedSources
                previewActions
                workflowLabels={workflowLabels}
                onWorkflowAnswer={(card, choice) => {
                  answerWorkflowStep(onSend, card, choice, workflowLabels.skip);
                }}
              />
            ),
          )}
          <ErrorNotice error={error} onRetry={onRetry} labels={{ retry: t("build.playground.chat.retry"), ...(errorText ? { generic: errorText } : {}) }} />
        </div>
      </ChatLog>
      <div className="shrink-0 border-t border-line bg-surface px-4 py-3">
        <div className="mx-auto max-w-2xl">
          <Composer onSend={submit} onStop={onStop} streaming={busy} lockedHint={workflowComposer.mode === "locked" ? workflowLabels.typingLocked : undefined} reply={replyCard ? { ...workflowReplyComposer(replyCard, workflowLabels), ...(replyIssue ? { error: replyIssue } : {}) } : undefined} onValueChange={(next) => { if (next) setReplyIssue(null); }} labels={{ input: t("chat.messageLabel"), send: t("chat.send"), hintSend: t("chat.enterToSend"), hintNewline: t("chat.newLine"), placeholder: t("build.playground.chat.placeholder"), stop: t("build.playground.chat.stop") }} />
          <p className="mt-1.5 text-center text-[11px] text-fg-subtle">{t("build.playground.chat.footer")}</p>
        </div>
      </div>
    </section>
  );
}
